import * as React from 'react';
import { ImageOff } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  mediaCornerRecipe,
  mediaFallbackRecipe,
  mediaFrameRecipe,
  mediaImageRecipe,
  thumbnailRecipe,
  type MediaFrameRecipeProps,
  type MediaImageRecipeProps,
  type ThumbnailRecipeProps,
} from '../recipes/image.recipe';
import { Icon } from './Icon';

export interface ThumbnailProps
  extends React.HTMLAttributes<HTMLDivElement>,
    ThumbnailRecipeProps {
  /** Nothing, null or an empty string all mean "no picture", and draw the fallback. */
  src?: string | null;
  /**
   * Empty by default: a thumbnail beside a title is decoration, and reading
   * "photo of 2005 Honda Accord" after "2005 Honda Accord" helps nobody.
   */
  alt?: string;
  /** The glyph shown when there is no picture. */
  fallback?: LucideIcon;
  /** Extra classes for the img itself — `grayscale` for an archived lot. */
  imageClassName?: string;
}

/**
 * A small fixed-size picture in a row, with its own "no picture" state.
 *
 *   <Thumbnail src={lot.photos[0]} />
 *   <Thumbnail src={null} size="md" shape="square" fallback={Truck} />
 *
 * Four rows in the auction pages wrote this as an img with one set of classes
 * and an ImageOff box with another, side by side in a ternary. Being one
 * element means the two states cannot drift apart in size.
 */
export const Thumbnail = React.forwardRef<HTMLDivElement, ThumbnailProps>(
  function Thumbnail(
    {
      src,
      alt = '',
      size,
      shape,
      fallback = ImageOff,
      imageClassName,
      className,
      ...props
    },
    ref
  ) {
    return (
      <div
        ref={ref}
        className={cn(thumbnailRecipe({ size, shape }), className)}
        {...props}
      >
        {src ? (
          <img
            src={src}
            alt={alt}
            className={cn(mediaImageRecipe(), imageClassName)}
          />
        ) : (
          <span className={mediaFallbackRecipe()}>
            <Icon icon={fallback} size={size === 'lg' ? 'md' : 'sm'} />
          </span>
        )}
      </div>
    );
  }
);

export interface MediaFrameProps
  extends React.HTMLAttributes<HTMLDivElement>,
    MediaFrameRecipeProps {
  src?: string | null;
  alt?: string;
  fallback?: LucideIcon;
  imageClassName?: string;
  /** Laid over the top-right corner: a status badge, a selected check. */
  corner?: React.ReactNode;
  /**
   * `cover` fills the frame and crops; `contain` shows the whole picture.
   * With `ratio="auto"` the frame takes the picture's own height, so
   * `contain` is the only fit that means anything there.
   */
  fit?: MediaImageRecipeProps['fit'];
  /** The source is a video: draw it with the browser's controls. */
  video?: boolean;
  /** Video only: show the play bar. On unless told otherwise. */
  controls?: boolean;
  /** The picture's `loading` attribute: `lazy` for a strip below the fold. */
  loading?: 'lazy' | 'eager';
  /**
   * The picture did not arrive. For a source that can 403 — a map tile whose
   * key is locked to one host — so the caller can show something else.
   */
  onImageError?: React.ReactEventHandler<HTMLImageElement>;
}

/**
 * A picture that fills its width at a fixed ratio, with room for an overlay.
 *
 *   <MediaFrame src={lot.photos[0]} alt={lot.title} ratio="4/3"
 *     corner={<Badge tone="warning">Archived</Badge>} />
 *   <MediaFrame src={photo.url} alt="Preview" ratio="auto" fit="contain" tone="none" />
 *   <MediaFrame src={clip.url} video ratio="auto" tone="none" />
 *
 * `children` render inside the frame too, positioned however they like —
 * for the caption bar along the bottom that `corner` does not cover.
 */
export const MediaFrame = React.forwardRef<HTMLDivElement, MediaFrameProps>(
  function MediaFrame(
    {
      src,
      alt = '',
      ratio,
      radius,
      bordered,
      tone,
      fit,
      video = false,
      controls = true,
      fallback = ImageOff,
      imageClassName,
      corner,
      loading,
      onImageError,
      className,
      children,
      ...props
    },
    ref
  ) {
    const mediaClassName = cn(
      mediaImageRecipe({ fit, ratio: ratio === 'auto' ? 'auto' : 'fixed' }),
      imageClassName
    );
    return (
      <div
        ref={ref}
        className={cn(
          mediaFrameRecipe({ ratio, radius, bordered, tone }),
          className
        )}
        {...props}
      >
        {src && video ? (
          // design-tokens-ignore: owned by MediaFrame
          // Metadata only, so a strip of clips does not fetch every file;
          // inline, so a phone plays it in the frame rather than full screen.
          <video
            src={src}
            controls={controls}
            preload="metadata"
            playsInline
            className={mediaClassName}
          />
        ) : src ? (
          <img
            src={src}
            alt={alt}
            loading={loading}
            onError={onImageError}
            className={mediaClassName}
          />
        ) : (
          <span className={mediaFallbackRecipe()}>
            <Icon icon={fallback} size="xl" />
          </span>
        )}
        {corner ? <span className={mediaCornerRecipe()}>{corner}</span> : null}
        {children}
      </div>
    );
  }
);
